import React, { useState, useEffect } from 'react';
import { Save, Loader2, Image, Upload } from 'lucide-react';
import { companyApi } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';

const AdminCompany = () => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await companyApi.get();
        setCompany(response.data);
      } catch (error) {
        console.error('Error fetching company:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  const handleChange = (field, value) => {
    setCompany((prev) => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (field, value) => {
    setCompany((prev) => ({
      ...prev,
      contact: { ...prev.contact, [field]: value },
    }));
  };

  const handleSocialChange = (field, value) => {
    setCompany((prev) => ({
      ...prev,
      social: { ...prev.social, [field]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await companyApi.update(company);
      setMessage('Company info saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error saving company info');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">Company Information</h1>
        {message && (
          <span className={`text-sm ${message.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Logo Section - Prominent */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <Image className="h-5 w-5 mr-2 text-gray-500" />
            <h2 className="text-lg font-medium text-gray-900">Company Logo</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Logo Preview */}
            <div className="space-y-4">
              <Label>Current Logo Preview</Label>
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 bg-gray-50">
                {company?.logo ? (
                  <div className="space-y-4">
                    {/* Light Background Preview */}
                    <div className="bg-white p-4 rounded border">
                      <p className="text-xs text-gray-400 mb-2">On Light Background</p>
                      <img
                        src={company.logo}
                        alt="Logo preview"
                        className="h-16 object-contain"
                      />
                    </div>
                    {/* Dark Background Preview */}
                    <div className="bg-gray-900 p-4 rounded">
                      <p className="text-xs text-gray-400 mb-2">On Dark Background (Footer)</p>
                      <img
                        src={company.logo}
                        alt="Logo preview dark"
                        className="h-16 object-contain brightness-0 invert"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-400">
                    <Upload className="h-12 w-12 mx-auto mb-2" />
                    <p>No logo set</p>
                  </div>
                )}
              </div>
            </div>

            {/* Logo URL Input */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="logo">Logo URL</Label>
                <Input
                  id="logo"
                  value={company?.logo || ''}
                  onChange={(e) => handleChange('logo', e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-gray-500">
                  Enter the URL of your logo image. Supported formats: PNG, JPG, SVG
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">Tips for best results:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Use a transparent PNG for best appearance</li>
                  <li>• Recommended size: 200-400px width</li>
                  <li>• Logo will be inverted (white) on dark backgrounds</li>
                  <li>• Host your logo on a reliable image hosting service</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name</Label>
              <Input
                id="name"
                value={company?.name || ''}
                onChange={(e) => handleChange('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                id="tagline"
                value={company?.tagline || ''}
                onChange={(e) => handleChange('tagline', e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={4}
                value={company?.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={company?.contact?.email || ''}
                onChange={(e) => handleContactChange('email', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={company?.contact?.phone || ''}
                onChange={(e) => handleContactChange('phone', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={company?.contact?.address || ''}
                onChange={(e) => handleContactChange('address', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Social Links</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="linkedin">LinkedIn URL</Label>
              <Input
                id="linkedin"
                value={company?.social?.linkedin || ''}
                onChange={(e) => handleSocialChange('linkedin', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instagram">Instagram URL</Label>
              <Input
                id="instagram"
                value={company?.social?.instagram || ''}
                onChange={(e) => handleSocialChange('instagram', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminCompany;
